import { LogOut, Menu, Moon, Search, Sun, X } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useSearch } from '../../contexts/SearchContext';
import { disconnectWallet } from '../../web3';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import { useToast } from '../ui/use-toast';
import { NotificationsPanel } from './NotificationsPanel';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

interface HeaderProps {
  title?: string;
  onMenuClick?: () => void;
}

export function Header({ title, onMenuClick }: HeaderProps) {
  const { theme, toggleTheme } = useTheme();
  const { searchQuery, performSearch, clearSearch, isSearching } = useSearch();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [localQuery, setLocalQuery] = useState(searchQuery);

  // Keep header input in sync when search is cleared (e.g. from SearchResults page)
  useEffect(() => {
    setLocalQuery(searchQuery);
  }, [searchQuery]);

  const handleLogout = () => {
    console.log('[Header] Logout clicked, disconnecting wallet and clearing session');
    disconnectWallet();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast({
      title: 'Logged out successfully',
      description: 'You have been logged out and wallet disconnected',
    });
    window.location.reload();
  };

  const handleSearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (localQuery.trim()) {
      await performSearch(localQuery.trim());
      navigate('/search');
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalQuery(e.target.value);
  };

  const handleSearchClear = () => {
    setLocalQuery('');
    clearSearch();
  };

  return (
    <header className='flex items-center justify-between h-14 sm:h-16 px-3 sm:px-4 lg:px-6 border-b border-border bg-card/50 backdrop-blur-sm'>
      {/* Left Section */}
      <div className='flex items-center gap-2 sm:gap-4 lg:gap-6 min-w-0 flex-shrink-0'>
        {/* Mobile Menu Button */}
        <Button
          variant='ghost'
          size='icon'
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onMenuClick) {
              onMenuClick();
            }
          }}
          className='lg:hidden flex-shrink-0 z-50 relative pointer-events-auto'
          type='button'
          aria-label='Toggle menu'
          aria-expanded={false}
        >
          <Menu className='w-5 h-5 pointer-events-none' />
        </Button>
        {title && (
          <h1 className='text-lg sm:text-xl font-semibold text-foreground truncate'>{title}</h1>
        )}
      </div>

      {/* Search - Centered */}
      <div className='hidden md:flex flex-1 justify-center max-w-lg mx-2 sm:mx-4 lg:mx-8'>
        <form onSubmit={handleSearchSubmit} className='relative w-full'>
          <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground' />
          <Input
            value={localQuery}
            onChange={handleSearchChange}
            placeholder='Search users, assets, transactions...'
            className='pl-10 pr-10 bg-muted/50 border-border focus:border-primary text-sm'
            disabled={isSearching}
          />
          {localQuery && (
            <Button
              type='button'
              variant='ghost'
              size='icon'
              onClick={handleSearchClear}
              className='absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 hover:bg-muted-foreground/20'
            >
              <X className='w-3 h-3' />
            </Button>
          )}
        </form>
      </div>

      {/* Right Section */}
      <div className='flex items-center gap-1 sm:gap-2 flex-shrink-0'>
        {/* Notifications */}
        <NotificationsPanel />

        {/* Theme Toggle */}
        <Button
          variant='ghost'
          size='icon'
          onClick={toggleTheme}
          className='transition-all duration-300 hidden sm:flex'
        >
          {theme === 'dark' ? (
            <Sun className='w-5 h-5 text-muted-foreground hover:text-warning transition-colors' />
          ) : (
            <Moon className='w-5 h-5 text-muted-foreground hover:text-primary transition-colors' />
          )}
        </Button>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' className='gap-1 sm:gap-2 px-1 sm:px-2'>
              <Avatar className='w-7 h-7 sm:w-8 sm:h-8'>
                <AvatarFallback className='bg-primary/20 text-primary text-xs sm:text-sm font-semibold'>
                  AD
                </AvatarFallback>
              </Avatar>
              <span className='text-sm font-medium text-foreground hidden lg:inline'>Admin</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' className='w-56'>
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>Profile Settings</DropdownMenuItem>
            <DropdownMenuItem>Team Management</DropdownMenuItem>
            <DropdownMenuItem>API Keys</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className='text-destructive focus:text-destructive'
              onClick={handleLogout}
            >
              <LogOut className='w-4 h-4 mr-2' />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
